import useScrollIntoView from '@/hooks/useScrollIntoView';
import DropdownButton from '@/storybook/components/FormCollection/components/DropdownButton/DropdownButton';
import DropdownList from '@/storybook/components/FormCollection/components/DropdownList/DropdownList';
import { useFloatingDropdownPanel } from '@/storybook/components/FormCollection/components/DropdownList/useFloatingDropdownPanel';
import { DropdownPosition, handleDropdownListPosition } from '@/storybook/components/FormCollection/utils/utils';
import cx from 'classnames';
import { type ReactNode, useCallback, useRef, useState } from 'react';
import styles from './OldTreeSelectFloatingLabel.module.css';
import { TreeSelectProps } from './TreeSelect.types';
import { useOldTreeSelect } from './hooks/useOldTreeSelect';

export type OldTreeSelectFloatingLabelProps<T> = Omit<TreeSelectProps<T>, 'label'> & {
  label?: ReactNode;
  state?: 'default' | 'filled' | 'error';
};

export default function OldTreeSelectFloatingLabel<T extends string>({
  value = [],
  onChange,
  options,
  isLoading,
  placeholder,
  searchable,
  disabled,
  label,
  tooltip,
  alignment = 'vertical',
  adobeEventTrackFunctions,
  onDropdownToggle,
  'data-testid': dataTestId,
  'dropdownLabelTestId': _dropdownLabelTestId,
  dropdownButtonTestId,
  dropdownListTestId,
  dropdownPosition,
  className,
  collapseSingleChild,
  selectionMode = 'standard',
  size = 'L',
  state = 'default',
  nestedKeySort = 'alphabetical',
  actionBarProps,
}: OldTreeSelectFloatingLabelProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownListPosition, setDropdownLitPosition] = useState<DropdownPosition>(dropdownPosition ?? 'down');

  const dropdownButtonRef = useRef<HTMLDivElement>(null);
  const { dropdownRef, panelRef } = useFloatingDropdownPanel({
    isOpen,
    onClose: () => {
      if (isOpen) onDropdownToggle?.(false);
      setIsOpen(false);
    },
  });

  useScrollIntoView({ elementRef: dropdownRef, isVisible: isOpen });

  const {
    fixedOptions,
    sortedNestedOptions,
    valueLabelList,
    searchInput,
    setSearchInput,
    searchResults,
    setUserOpenedBranches,
    renderNestedOptions,
  } = useOldTreeSelect<T>({
    options,
    value,
    onChange,
    collapseSingleChild,
    selectionMode,
    nestedKeySort,
    disabled,
    treeListItemClassName: styles.treeListItem,
  });

  const handleToggleDropdown = useCallback(() => {
    if (disabled) return;
    const newState = !isOpen;
    setIsOpen(newState);
    onDropdownToggle?.(newState);
    if (newState) {
      handleDropdownListPosition(dropdownPosition, dropdownRef, setDropdownLitPosition);
      setSearchInput('');
      setUserOpenedBranches(new Set());
    }
  }, [disabled, isOpen, dropdownPosition, dropdownRef, onDropdownToggle, setSearchInput, setUserOpenedBranches]);

  const handleOnClearValue = useCallback(() => {
    if (!disabled) onChange([]);
  }, [disabled, onChange]);

  const displayLabel = label || placeholder;
  const isLabelFloating = value.length > 0 || isOpen;

  return (
    <div
      ref={dropdownRef}
      className={cx(styles.floatingLabelContainer, className)}
      data-alignment={alignment}
      data-testid={dataTestId}
      onClick={adobeEventTrackFunctions}
      data-state={state}
    >
      <section className={styles.dropdownContainer}>
        {displayLabel && (
          <label className={styles.floatingLabel} data-floating={isLabelFloating} data-has-value={value.length > 0}>
            {displayLabel}
          </label>
        )}
        <div className={styles.buttonWrapper}>
          <DropdownButton
            ref={dropdownButtonRef}
            type="multiSelect"
            size={size}
            placeholder=""
            numOfValue={value?.length}
            active={isOpen}
            onClick={!disabled ? handleToggleDropdown : undefined}
            onClear={handleOnClearValue}
            clearable={true}
            disabled={disabled}
            data-testid={dropdownButtonTestId}
          >
            <div className={styles.value}>
              <span className={styles.valueText}>{valueLabelList.join(', ')}</span>
            </div>
          </DropdownButton>
        </div>
        <DropdownList
          isOpen={isOpen}
          isLoading={isLoading}
          disabled={!!disabled}
          position={dropdownListPosition}
          isSearchable={searchable}
          searchValue={searchInput}
          onSearchValueChange={setSearchInput}
          numberOfSearchResults={searchInput ? searchResults.size : fixedOptions.length}
          data-testid={dropdownListTestId}
          listRef={null}
          dropdownTriggerRef={dropdownButtonRef}
          isFloating
          panelRef={panelRef}
          optionsCount={options.length}
          actionBarProps={actionBarProps}
        >
          <div className={styles.treeSelectList}>{renderNestedOptions(sortedNestedOptions)}</div>
        </DropdownList>
      </section>
      {tooltip && <div className={styles.tooltip}>{tooltip}</div>}
    </div>
  );
}
